import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { DeepMap, FieldValues } from '../../../Vendor/react-hook-form/dist'
import { encrypt } from '../../Common/Utils/Security'
import { HASHED_PASSWORD_STRING, INTEGRATION_TYPE_VALUES, INTEGRATION_TYPES } from '../Constants/Constants'
import { APICommunicationSettings, ApplicationCallRecIntegrationData, CallRecIntegrationRequest } from '../Types/Types'

const mapIntegrationToString = (integrationType: number): string => {
    const integrationValues: { [key: number]: string } = {
        [INTEGRATION_TYPE_VALUES.DUBBER]: INTEGRATION_TYPES.DUBBER,
        [INTEGRATION_TYPE_VALUES.UNKNOWN]: INTEGRATION_TYPES.UNKNOWN
    }

    return integrationValues[integrationType]
}

const createDubberCommunicationsSettings = (
    state: ApplicationCallRecIntegrationData,
    sessionNonce: Definable<string>,
    method?: RESTMethods,
    dirtyFields?: DeepMap<FieldValues, true>
): APICommunicationSettings => {
    let communications: APICommunicationSettings = {
        IPAddrOrHost1: state?.dubberCommunicationsSettings?.dubberLocationUrl,
        APIParameter1: state?.dubberCommunicationsSettings?.apiParametersOne ?? EMPTY_STRING,
        APIParameter2: state?.dubberCommunicationsSettings?.apiParametersTwo ?? EMPTY_STRING
    }

    if (method === RESTMethods.PUT) {
        // The developer credentials are derived from the selected Dubber location, so a location change must
        // re-send them even though the effect only repopulated the fields (via setValue, leaving them clean).
        // Without this, the PUT omits them and the backend keeps the previous location's credentials (APD-13064).
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const locationChanged: boolean = Boolean(dirtyFields?.dubberCommunicationsSettings?.dubberLocationUrl)

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (locationChanged || dirtyFields?.dubberCommunicationsSettings?.developerAuthPassword) {
            const developerAuthPassword: string =
                state?.dubberCommunicationsSettings?.developerAuthPassword ?? EMPTY_STRING

            // Pass the hashed placeholder through unencrypted so the backend substitutes the location default;
            // only a real, user-entered password is encrypted.
            communications.AuthPassword1 =
                developerAuthPassword === HASHED_PASSWORD_STRING
                    ? HASHED_PASSWORD_STRING
                    : encrypt(developerAuthPassword, sessionNonce)
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (dirtyFields?.dubberCommunicationsSettings?.accountAuthPassword) {
            communications.AuthPassword2 = encrypt(
                state?.dubberCommunicationsSettings?.accountAuthPassword ?? EMPTY_STRING,
                sessionNonce
            )
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (locationChanged || dirtyFields?.dubberCommunicationsSettings?.developerAuthUsername) {
            communications.AuthUserID1 = state?.dubberCommunicationsSettings?.developerAuthUsername ?? EMPTY_STRING
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (dirtyFields?.dubberCommunicationsSettings?.accountAuthUsername) {
            communications.AuthUserID2 = state?.dubberCommunicationsSettings?.accountAuthUsername ?? EMPTY_STRING
        }
    } else {
        communications = {
            ...communications,
            AuthUserID1: state?.dubberCommunicationsSettings?.developerAuthUsername ?? EMPTY_STRING,
            AuthUserID2: state?.dubberCommunicationsSettings?.accountAuthUsername ?? EMPTY_STRING,
            AuthPassword1: encrypt(state?.dubberCommunicationsSettings?.developerAuthPassword ?? '', sessionNonce),
            AuthPassword2: encrypt(state?.dubberCommunicationsSettings?.accountAuthPassword ?? '', sessionNonce)
        }
    }

    return communications
}

const stateMapper = (
    state: ApplicationCallRecIntegrationData,
    sessionNonce: Definable<string>,
    method?: RESTMethods,
    dirtyFields?: DeepMap<FieldValues, true>
): CallRecIntegrationRequest => {
    const request: CallRecIntegrationRequest = {
        CallRecIntType: mapIntegrationToString(state?.callRecIntegrationDetails?.integrationType ?? 0),
        Description: state?.callRecIntegrationDetails?.description ?? '',
        APICommunicationSettings: createDubberCommunicationsSettings(state, sessionNonce, method, dirtyFields)
    }

    if (method === RESTMethods.PUT) {
        request.TelSysID = Number(state?.scopeAssignments?.telephonyServer)
        request.PartIDInTelSys = Number(state?.scopeAssignments?.partition)
    }

    return request
}

export default stateMapper
